import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HEADER_TOOL_BTN_CLASS, HEADER_TOOL_ICON_CLASS } from "@/components/layout/shell/HeaderToolButton";
import { THEME_OPTIONS, getThemeOption, type ThemePreset } from "@/lib/themes";
import { useTheme } from "@/providers/theme-provider";

interface ThemePickerProps {
  /** Icon dropdown for header, or full cards for settings. */
  variant?: "icon" | "dropdown" | "compact" | "cards";
  className?: string;
}

export function ThemePicker({ variant = "cards", className }: ThemePickerProps) {
  const { theme, setTheme } = useTheme();

  if (variant === "icon" || variant === "dropdown" || variant === "compact") {
    const current = getThemeOption(theme);
    const CurrentIcon = current.icon;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title={`Theme: ${current.label}`}
            className={cn(
              HEADER_TOOL_BTN_CLASS,
              "bg-card text-muted-foreground ring-border hover:bg-muted/50 hover:text-foreground",
              className,
            )}
          >
            <CurrentIcon className={HEADER_TOOL_ICON_CLASS} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 p-1">
          {THEME_OPTIONS.map((option) => {
            const Icon = option.icon;
            const active = theme === option.id;
            return (
              <DropdownMenuItem
                key={option.id}
                onClick={() => setTheme(option.id as ThemePreset)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2 py-2",
                  active && "bg-primary/8 text-foreground",
                )}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-semibold">{option.label}</span>
                  <p className="truncate text-[11px] text-muted-foreground">{option.description}</p>
                </div>
                {active && (
                  <span className="shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
                    Active
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className={cn("grid gap-3 sm:grid-cols-2", className)}>
      {THEME_OPTIONS.map((option) => {
        const Icon = option.icon;
        const active = theme === option.id;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => setTheme(option.id as ThemePreset)}
            className={cn(
              "flex items-start gap-3 rounded-xl border p-4 text-left transition-all",
              active
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-border bg-card hover:border-primary/30 hover:bg-muted/40",
            )}
          >
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{option.label}</span>
                {active && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                    Active
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
              <div className="flex gap-1 mt-2">
                {option.swatch.map((color) => (
                  <span
                    key={color}
                    className="h-3 w-3 rounded-full ring-1 ring-border"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
